import type {RuleSeverity} from '@github-ui/code-scanning-shared/types/rule-severity'
import {FilterIcon, KebabHorizontalIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button, CounterLabel, IconButton, SegmentedControl} from '@primer/react'
import type {FC} from 'react'

interface FindingsFilterBarProps {
  openCount: number
  resolvedCount: number
  displayedCount: number
  selectedVisibility: 'open' | 'resolved'
  availableSeverities: RuleSeverity[]
  availableSources: string[]
  selectedSeverities: Set<RuleSeverity>
  selectedSources: Set<string>
  handleVisibilityChange: (visibility: 'open' | 'resolved') => void
  handleSeverityToggle: (severity: RuleSeverity) => void
  handleSourceToggle: (source: string) => void
  handleDismiss: () => void
  handleReopen: () => void
  handleFix: () => void
}

const SEVERITY_LABELS: Partial<Record<RuleSeverity, string>> = {
  failure: 'Failure',
  critical: 'Critical',
  high: 'High',
  error: 'Error',
  medium: 'Medium',
  warning: 'Warning',
  low: 'Low',
  note: 'Note',
  nit: 'Nit',
  notice: 'Notice',
  none: 'None',
}

const countLabel = (count: number, activeFilterCount: number) =>
  activeFilterCount > 0 ? `${count} ${count === 1 ? 'finding' : 'findings'}` : 'all'

export const FindingsFilterBar: FC<FindingsFilterBarProps> = ({
  openCount,
  resolvedCount,
  displayedCount,
  selectedVisibility,
  availableSeverities,
  availableSources,
  selectedSeverities,
  selectedSources,
  handleVisibilityChange,
  handleSeverityToggle,
  handleSourceToggle,
  handleDismiss,
  handleReopen,
  handleFix,
}) => {
  const activeFilterCount = selectedSeverities.size + selectedSources.size

  return (
    <div className="d-flex flex-items-center flex-justify-between tmp-mb-3">
      <h2 id="findings-heading" className="f3 text-semibold">
        Findings
      </h2>
      <div className="d-flex gap-2">
        <SegmentedControl aria-label="Finding visibility">
          <SegmentedControl.Button
            selected={selectedVisibility === 'open'}
            onClick={() => handleVisibilityChange('open')}
            count={openCount}
          >
            Open
          </SegmentedControl.Button>
          <SegmentedControl.Button
            selected={selectedVisibility === 'resolved'}
            onClick={() => handleVisibilityChange('resolved')}
            count={resolvedCount}
          >
            Resolved
          </SegmentedControl.Button>
        </SegmentedControl>
        <ActionMenu>
          <ActionMenu.Anchor>
            <Button
              variant="invisible"
              leadingVisual={FilterIcon}
              aria-label={`Filter findings${activeFilterCount > 0 ? ` (${activeFilterCount} active)` : ''}`}
            >
              {activeFilterCount > 0 && <CounterLabel>{activeFilterCount}</CounterLabel>}
            </Button>
          </ActionMenu.Anchor>
          <ActionMenu.Overlay>
            <ActionList>
              {availableSeverities.length > 0 && (
                <ActionList.Group selectionVariant="multiple">
                  <ActionList.GroupHeading>Severity</ActionList.GroupHeading>
                  {availableSeverities.map(severity => (
                    <ActionList.Item
                      key={severity}
                      selected={selectedSeverities.has(severity)}
                      onSelect={() => handleSeverityToggle(severity)}
                    >
                      {SEVERITY_LABELS[severity] ?? severity}
                    </ActionList.Item>
                  ))}
                </ActionList.Group>
              )}
              {availableSeverities.length > 0 && availableSources.length > 0 && <ActionList.Divider />}
              {availableSources.length > 0 && (
                <ActionList.Group selectionVariant="multiple">
                  <ActionList.GroupHeading>Sources</ActionList.GroupHeading>
                  {availableSources.map(source => (
                    <ActionList.Item
                      key={source}
                      selected={selectedSources.has(source)}
                      onSelect={() => handleSourceToggle(source)}
                    >
                      {source}
                    </ActionList.Item>
                  ))}
                </ActionList.Group>
              )}
            </ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
        <ActionMenu>
          <ActionMenu.Anchor>
            <IconButton icon={KebabHorizontalIcon} variant="invisible" aria-label="More actions" />
          </ActionMenu.Anchor>
          <ActionMenu.Overlay align="end" width="auto">
            <ActionList>
              <ActionList.Item onSelect={handleFix} disabled={selectedVisibility !== 'open' || displayedCount === 0}>
                {`Fix ${countLabel(displayedCount, activeFilterCount)} with Copilot`}
              </ActionList.Item>
              {selectedVisibility === 'open' && (
                <ActionList.Item onSelect={handleDismiss} disabled={displayedCount === 0}>
                  {`Dismiss ${countLabel(displayedCount, activeFilterCount)}`}
                </ActionList.Item>
              )}
              {selectedVisibility === 'resolved' && (
                <ActionList.Item onSelect={handleReopen} disabled={displayedCount === 0}>
                  {`Reopen ${countLabel(displayedCount, activeFilterCount)}`}
                </ActionList.Item>
              )}
            </ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
      </div>
    </div>
  )
}
